import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';
import './UserPermissionModal.css';

const UserPermissionModal = ({ show, user, onClose, onSuccess }) => {
    const [role, setRole] = useState('STAFF');
    // 어느 건물주의 계정인가. 역할이 건물주일 때만 뜻이 있다.
    const [clientId, setClientId] = useState('');
    const [clients, setClients] = useState([]);
    const [permissions, setPermissions] = useState({
        manage_facilities: false,
        manage_checklists: false,
        manage_users: false,
        manage_issues: false,
        manage_reports: false,
        manage_leases: false,
        // 건물주가 이슈를 올릴 수 있는가. 다른 열쇠들과 성격이 다르다 --
        // 관리자에게 무엇을 맡길지가 아니라, 바깥 사람에게 무엇을 허락할지다.
        client_report_issue: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showAlert } = useModal();

    // 건물주 목록은 열 때 한 번만. 역할을 건물주로 바꾸는 순간 고를 것이
    // 있어야 한다.
    useEffect(() => {
        if (!show) return;
        api
            .get('/clients')
            .then((res) => setClients(res.data || []))
            .catch((err) => console.error('Failed to load clients', err));
    }, [show]);

    useEffect(() => {
        if (show && user) {
            setRole(user.role);
            // Parse permissions if exists
            const userPerms = user.permissions || {};
            setPermissions({
                manage_facilities: !!userPerms.manage_facilities,
                manage_checklists: !!userPerms.manage_checklists,
                manage_users: !!userPerms.manage_users,
                manage_issues: !!userPerms.manage_issues,
                manage_reports: !!userPerms.manage_reports,
                manage_leases: !!userPerms.manage_leases,
                client_report_issue: !!userPerms.client_report_issue,
            });
            setClientId(user.clientId ? String(user.clientId) : '');
        }
    }, [show, user]);

    const handlePermissionChange = (key) => {
        setPermissions(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const data = {
                role,
                // 건물주에게는 자기 열쇠만, 중간 관리자에게는 관리 열쇠만 준다.
                permissions:
                    role === 'CLIENT'
                        ? { client_report_issue: permissions.client_report_issue }
                        : role === 'INTERMEDIATE_ADMIN'
                          ? permissions
                          : null,
                // 건물주가 아니면 매인 건물주를 푼다. 역할만 바꾸고 clientId 가
                // 남으면 직원인데 남의 건물만 보이는 계정이 된다.
                clientId: role === 'CLIENT' ? Number(clientId) || null : null,
            };
            if (role === 'CLIENT' && !data.clientId) {
                showAlert('알림', '어느 건물주인지 골라 주세요.', 'warning');
                setIsSubmitting(false);
                return;
            }

            await api.patch(`/users/${user.id}`, data);
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to update permissions:', error);
            showAlert('오류', '권한 수정에 실패했습니다.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold">권한 설정</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {user && (
                    <Form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <h6 className="fw-bold mb-1">{user.name}</h6>
                            <div className="text-muted small">{user.email}</div>
                        </div>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold mb-3">역할 선택</Form.Label>
                            <div className="role-selection-grid">
                                {[
                                    { id: 'STAFF', icon: 'bi-person', name: '일반 작업자', desc: '점검 및 이슈 등록' },
                                    { id: 'INTERMEDIATE_ADMIN', icon: 'bi-person-badge', name: '중간 관리자', desc: '부분 메뉴 관리' },
                                    { id: 'SUPER_ADMIN', icon: 'bi-shield-lock', name: '최고 관리자', desc: '모든 기능 관리' },
                                    { id: 'OWNER', icon: 'bi-crown', name: '소유자', desc: '시스템 전체 제어' },
                                    // 회사 사람이 아니다. 자기 건물의 결과만 본다.
                                    { id: 'CLIENT', icon: 'bi-person-badge', name: '건물주', desc: '자기 건물만 보기' }
                                ].map(item => (
                                    <div
                                        key={item.id}
                                        className={`role-card ${role === item.id ? 'active' : ''}`}
                                        onClick={() => setRole(item.id)}
                                    >
                                        <i className={`bi ${item.icon}`}></i>
                                        <div className="role-name">{item.name}</div>
                                        <div className="role-desc">{item.desc}</div>
                                    </div>
                                ))}
                            </div>
                        </Form.Group>

                        {role === 'CLIENT' && (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold mb-2">어느 건물주</Form.Label>
                                <Form.Select
                                    value={clientId}
                                    onChange={(e) => setClientId(e.target.value)}
                                >
                                    <option value="">고르세요</option>
                                    {clients.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </Form.Select>
                                <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                                    이 계정은 그 건물주의 건물만 봅니다. 점검 계획·점검표·구역·호실은 열리지 않습니다.
                                </div>
                                <Form.Check
                                    className="mt-3"
                                    type="switch"
                                    id="perm-client-issue"
                                    label="이슈 등록 허용"
                                    checked={permissions.client_report_issue}
                                    onChange={() => handlePermissionChange('client_report_issue')}
                                />
                            </Form.Group>
                        )}

                        {role === 'INTERMEDIATE_ADMIN' && (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold mb-2">세부 권한</Form.Label>
                                <div className="d-flex flex-column gap-2 bg-light p-3 rounded">
                                    <Form.Check
                                        type="switch"
                                        id="perm-facilities"
                                        label="시설 관리"
                                        checked={permissions.manage_facilities}
                                        onChange={() => handlePermissionChange('manage_facilities')}
                                    />
                                    <Form.Check
                                        type="switch"
                                        id="perm-checklists"
                                        label="점검표 관리"
                                        checked={permissions.manage_checklists}
                                        onChange={() => handlePermissionChange('manage_checklists')}
                                    />
                                    <Form.Check
                                        type="switch"
                                        id="perm-workers"
                                        label="사용자 관리"
                                        checked={permissions.manage_users}
                                        onChange={() => handlePermissionChange('manage_users')}
                                    />
                                    <Form.Check
                                        type="switch"
                                        id="perm-issues"
                                        label="이슈 관리"
                                        checked={permissions.manage_issues}
                                        onChange={() => handlePermissionChange('manage_issues')}
                                    />
                                    {/* 점검 기록은 무슨 일이 있었는지에 대한 증거다.
                                        지우는 것을 막지는 않지만, 아무나 줄 권한은
                                        아니라서 따로 켠다. */}
                                    <Form.Check
                                        type="switch"
                                        id="perm-reports"
                                        label="점검 기록 삭제·복구"
                                        checked={permissions.manage_reports}
                                        onChange={() => handlePermissionChange('manage_reports')}
                                    />
                                    {/* 임대료와 임차인 연락처가 들어 있다. 점검하러
                                        다니는 사람이 볼 것이 아니다. */}
                                    <Form.Check
                                        type="switch"
                                        id="perm-leases"
                                        label="임대차 관리"
                                        checked={permissions.manage_leases}
                                        onChange={() => handlePermissionChange('manage_leases')}
                                    />
                                </div>
                            </Form.Group>
                        )}

                        <div className="d-flex justify-content-end mt-4">
                            <Button variant="light" onClick={onClose} className="me-2">
                                취소
                            </Button>
                            <Button variant="primary" type="submit" disabled={isSubmitting}>
                                저장
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default UserPermissionModal;
